import { useState, useEffect, useRef } from "react";

const STATS = [
  { value: "₹18,400", label: "Average urban family food spend/month", sub: "across 5 apps — invisible to you" },
  { value: "56%", label: "Gold rose in 2025 alone", sub: "while your salary grew 0.07%" },
  { value: "₹2,670", label: "Wasted on impulse Zomato orders", sub: "per month — per family, on average" },
  { value: "6%", label: "Annual inflation rate", sub: "your savings account earns 2.5%" },
];

const TRUTHS = [
  { icon: "↑", col: "#E24B4A", text: "Your food bill grows 12% every year" },
  { icon: "↓", col: "#E24B4A", text: "Your real salary grew 0.07% since 2020" },
  { icon: "✗", col: "#BA7517", text: "You have no idea where ₹18,000/month goes" },
  { icon: "✓", col: "#1D9E75", text: "CartIQ 360 shows you — in 60 seconds" },
];

function Counter({ target, duration = 1800, prefix = "", suffix = "" }) {
  const [display, setDisplay] = useState("0");
  const started = useRef(false);
  const ref = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const isNum = !isNaN(parseFloat(target));
        if (!isNum) { setDisplay(target); return; }
        const end = parseFloat(target);
        const start = Date.now();
        const tick = () => {
          const p = Math.min((Date.now() - start) / duration, 1);
          const ease = 1 - Math.pow(1 - p, 3);
          const cur = end * ease;
          setDisplay(cur >= 1000 ? Math.round(cur).toLocaleString("en-IN") : cur.toFixed(cur < 10 ? 1 : 0));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

export default function CartIQFOMO() {
  const [uploaded, setUploaded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) setUploaded(true);
  };

  return (
    <div data-testid="fomo-page" style={{
      minHeight: "100vh",
      background: "#0A0A0F",
      fontFamily: "'Georgia', 'Times New Roman', serif",
      color: "#F0EDE8",
      overflowX: "hidden",
    }}>

      {/* ── NAV ───────────────────────────────── */}
      <nav style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 32px", borderBottom: "1px solid #1A1A28",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
          <span style={{ fontSize: "22px", fontWeight: "900", color: "#FF6B00", fontFamily: "sans-serif", letterSpacing: "-1px" }}>CartIQ</span>
          <span style={{ fontSize: "13px", color: "#555", fontFamily: "sans-serif" }}>360</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <a href="#upload" data-testid="fomo-nav-try-free" style={{ padding: "8px 20px", borderRadius: "6px", background: "#FF6B00", color: "#fff", fontSize: "13px", fontFamily: "sans-serif", fontWeight: "700", textDecoration: "none" }}>
            Try Free
          </a>
        </div>
      </nav>

      {/* ── BREAKING NEWS TICKER ──────────────── */}
      <div style={{
        background: "#E24B4A", padding: "9px 0", overflow: "hidden",
        display: "flex", alignItems: "center", gap: "0",
      }}>
        <div style={{
          background: "#B03030", padding: "9px 18px", fontSize: "11px",
          fontWeight: "700", color: "#fff", letterSpacing: "2px",
          fontFamily: "sans-serif", whiteSpace: "nowrap", flexShrink: 0,
        }}>BREAKING</div>
        <div style={{ overflow: "hidden", flex: 1 }}>
          <div style={{
            display: "inline-block",
            animation: "ticker 28s linear infinite",
            whiteSpace: "nowrap", fontSize: "13px", color: "#fff",
            fontFamily: "sans-serif", fontWeight: "500", paddingLeft: "40px",
          }}>
            CA warns: "Salary growing but wealth shrinking" — Indians must shift from savings to growth mindset &nbsp;•&nbsp;
            Real wages contracted 0.07% while gold rose 56% in 2025 &nbsp;•&nbsp;
            Savings accounts earn 2.5% — inflation runs at 6% — your money loses value every day &nbsp;•&nbsp;
            Urban families spend ₹18,000+/month on food across 5 apps — with zero visibility &nbsp;•&nbsp;
            "You're saving, yes — but your wealth is eroding every single year" — CA Abhishek Walia &nbsp;•&nbsp;
          </div>
        </div>
        <style>{`@keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }`}</style>
      </div>

      {/* ── HERO ──────────────────────────────── */}
      <div style={{
        maxWidth: "900px", margin: "0 auto", padding: "80px 24px 40px",
        opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(24px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}>

        {/* Eyebrow */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          background: "#1A0800", border: "1px solid #FF6B0040",
          borderRadius: "20px", padding: "6px 16px", marginBottom: "32px",
        }}>
          <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#E24B4A", animation: "pulse 1.5s infinite" }} />
          <span style={{ fontSize: "12px", color: "#FF9560", fontFamily: "sans-serif", letterSpacing: "1px" }}>
            WHILE YOU'RE READING THIS, YOUR MONEY IS LOSING VALUE
          </span>
          <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
        </div>

        {/* Main headline */}
        <h1 style={{
          fontSize: "clamp(36px, 6vw, 68px)",
          lineHeight: 1.08, margin: "0 0 24px",
          fontWeight: "normal", letterSpacing: "-1.5px",
        }}>
          Your salary grows.<br />
          <span style={{ color: "#E24B4A" }}>Your food bill grows faster.</span><br />
          <span style={{ color: "#FF6B00" }}>You see neither.</span>
        </h1>

        {/* Sub */}
        <p style={{
          fontSize: "clamp(16px, 2.2vw, 21px)", color: "#888",
          lineHeight: 1.65, margin: "0 0 48px", maxWidth: "600px",
          fontStyle: "italic",
        }}>
          Urban Indian families spend ₹18,000+ every month across Blinkit, Zepto,
          Zomato and Swiggy — with zero visibility into where it goes, why it grows,
          and how much is being silently wasted.
        </p>

        {/* Truth cards */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "12px", marginBottom: "56px",
        }}>
          {TRUTHS.map((t, i) => (
            <div key={i} data-testid={`fomo-truth-${i}`} style={{
              background: "#0F0F1A", border: `1px solid ${t.col}30`,
              borderLeft: `3px solid ${t.col}`,
              borderRadius: "8px", padding: "16px",
              opacity: visible ? 1 : 0,
              transition: `opacity 0.5s ease ${0.2 + i * 0.1}s`,
            }}>
              <div style={{ fontSize: "18px", color: t.col, marginBottom: "6px", fontFamily: "sans-serif", fontWeight: "700" }}>{t.icon}</div>
              <div style={{ fontSize: "13px", color: "#CCC", lineHeight: 1.5, fontFamily: "sans-serif" }}>{t.text}</div>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px", marginBottom: "64px",
          borderTop: "1px solid #1A1A28", borderBottom: "1px solid #1A1A28",
          padding: "32px 0",
        }}>
          {STATS.map((s, i) => (
            <div key={i} data-testid={`fomo-stat-${i}`} style={{ textAlign: "center" }}>
              <div style={{
                fontSize: "clamp(28px, 4vw, 40px)", fontWeight: "700",
                color: i === 3 ? "#E24B4A" : "#FF6B00",
                fontFamily: "sans-serif", letterSpacing: "-1px", lineHeight: 1,
                marginBottom: "8px",
              }}>
                <Counter target={s.value.replace(/[₹%,]/g, "")}
                  prefix={s.value.startsWith("₹") ? "₹" : ""}
                  suffix={s.value.endsWith("%") ? "%" : ""}
                  duration={1600} />
              </div>
              <div style={{ fontSize: "13px", color: "#CCC", fontFamily: "sans-serif", marginBottom: "4px" }}>{s.label}</div>
              <div style={{ fontSize: "11px", color: "#555", fontFamily: "sans-serif", fontStyle: "italic" }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* The pivot */}
        <div style={{
          textAlign: "center", marginBottom: "48px",
        }}>
          <p style={{
            fontSize: "clamp(20px, 3vw, 32px)", lineHeight: 1.4,
            margin: "0 0 8px", letterSpacing: "-0.5px",
          }}>
            What if you could see <span style={{ color: "#FF6B00" }}>everything</span> —
          </p>
          <p style={{
            fontSize: "clamp(20px, 3vw, 32px)", lineHeight: 1.4,
            color: "#666", margin: "0", fontStyle: "italic",
          }}>
            in 60 seconds, for free, right now?
          </p>
        </div>

        {/* ── UPLOAD ZONE ───────────────────── */}
        <div id="upload">
          {!uploaded ? (
            <div
              data-testid="fomo-upload-zone"
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => setUploaded(true)}
              style={{
                border: `2px dashed ${dragging ? "#FF6B00" : "#2A2A3E"}`,
                borderRadius: "16px",
                padding: "56px 32px",
                textAlign: "center",
                cursor: "pointer",
                background: dragging ? "#1A0800" : "#0C0C18",
                transition: "all 0.2s",
              }}>
              <div style={{ fontSize: "40px", marginBottom: "16px" }}>📄</div>
              <div style={{ fontSize: "20px", color: "#FF6B00", fontFamily: "sans-serif", fontWeight: "700", marginBottom: "8px" }}>
                Drop your Blinkit / Zepto / Zomato invoices here
              </div>
              <div style={{ fontSize: "14px", color: "#666", fontFamily: "sans-serif", marginBottom: "24px" }}>
                PDF invoices from any food app · Multiple files accepted · Free analysis
              </div>
              <div style={{
                display: "inline-block", background: "#FF6B00",
                color: "#fff", padding: "14px 36px", borderRadius: "8px",
                fontSize: "15px", fontFamily: "sans-serif", fontWeight: "700",
              }}>
                Analyse My Food Spend Free →
              </div>
              <div style={{ marginTop: "16px", fontSize: "12px", color: "#444", fontFamily: "sans-serif" }}>
                No signup. No app download. No credit card.
              </div>
            </div>
          ) : (
            <div data-testid="fomo-upload-processing" style={{
              background: "#0C1A0C", border: "2px solid #1D9E75",
              borderRadius: "16px", padding: "40px", textAlign: "center",
            }}>
              <div style={{ marginBottom: "20px" }}>
                <div style={{
                  height: "6px", background: "#1A1A28", borderRadius: "3px",
                  overflow: "hidden", maxWidth: "400px", margin: "0 auto 16px",
                }}>
                  <div style={{
                    height: "100%", background: "#FF6B00", borderRadius: "3px",
                    animation: "load 2s ease forwards",
                  }} />
                  <style>{`@keyframes load{from{width:0}to{width:100%}}`}</style>
                </div>
              </div>
              <div style={{ fontSize: "18px", color: "#1D9E75", fontFamily: "sans-serif", fontWeight: "700", marginBottom: "8px" }}>
                Reading your invoices...
              </div>
              <div style={{ fontSize: "14px", color: "#666", fontFamily: "sans-serif" }}>
                Extracting every item, price and pattern from your orders
              </div>
            </div>
          )}
        </div>

        {/* Social proof */}
        <div style={{
          display: "flex", gap: "24px", justifyContent: "center",
          marginTop: "32px", flexWrap: "wrap",
        }}>
          {["Works with Blinkit", "Works with Zepto", "Works with Zomato", "Works with Swiggy"].map((t, i) => (
            <div key={i} style={{
              fontSize: "12px", color: "#555", fontFamily: "sans-serif",
              display: "flex", alignItems: "center", gap: "6px",
            }}>
              <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#1D9E75" }} />
              {t}
            </div>
          ))}
        </div>

        {/* Source attribution */}
        <div style={{
          marginTop: "64px", padding: "20px", background: "#0C0C18",
          borderRadius: "8px", borderLeft: "2px solid #333",
        }}>
          <div style={{ fontSize: "11px", color: "#444", fontFamily: "sans-serif", lineHeight: 1.6 }}>
            Sources: CA Abhishek Walia via BusinessToday · Akshat Shrivastava / Wisdom Hatch via BusinessToday ·
            Mercer Salary Survey 2025 · PLFS wage data FY22–24 · Mint / LiveMint food inflation data
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        borderTop: "1px solid #1A1A28", padding: "24px 32px",
        textAlign: "center", marginTop: "40px",
      }}>
        <div style={{ fontSize: "12px", color: "#444", fontFamily: "sans-serif" }}>
          CartIQ 360 · Solutionist · Born in India. Built for the World.
        </div>
      </div>
    </div>
  );
}
